import React, {useEffect, useState} from "react";
import {Checkbox, Form, Input, Select, message, Row, Col} from "antd";
import server from "../../../../../server";

interface ISelectedAddressProps {
    countries: string[];
    states: string[];
    cities: string[];
}

interface IAddressConfig {
    sameAsPresent: boolean;
    selectedAddress: {
        presentAddress: ISelectedAddressProps;
        permanentAddress: ISelectedAddressProps;
    };
}

const initialSelectedAddress = {
    presentAddress: {countries: [], states: [], cities: []},
    permanentAddress: {countries: [], states: [], cities: []},
};

const AddressView = ({form}: { form: any }) => {
    const [addressConfig, setAddressConfig] = useState<IAddressConfig>({
        sameAsPresent: false,
        selectedAddress: initialSelectedAddress,
    });

    /** Load Countries */
    useEffect(() => {
        fetchAllCountries();
    }, []);

    /** Fetch all countries */
    async function fetchAllCountries() {
        try {
            const countryResponse = await server.utils.countriesnow.getCountries();
            const allCountries = countryResponse.data.map((c: any) => c.country);
            setAddressConfig((prev) => ({
                ...prev,
                selectedAddress: {
                    presentAddress: {...prev.selectedAddress.presentAddress, countries: allCountries},
                    permanentAddress: {...prev.selectedAddress.permanentAddress, countries: allCountries},
                },
            }));
        } catch (e) {
            message.error("Failed to fetch countries");
        }
    }

    /** Fetch states */
    async function fetchAllStates(addressType: "present" | "permanent", country: string) {
        try {
            form.setFieldsValue({
                address: {
                    ...form.getFieldValue("address"),
                    [addressType]: {...form.getFieldValue(["address", addressType]), state: "", city: ""},
                },
            });
            const stateResponse = await server.utils.countriesnow.getStates(country);
            const allStates = stateResponse.data.states.map((s: any) => s.name);

            setAddressConfig((prev) => ({
                ...prev,
                selectedAddress: {
                    ...prev.selectedAddress,
                    [`${addressType}Address`]: {
                        ...prev.selectedAddress[`${addressType}Address`],
                        states: allStates,
                        cities: [],
                    },
                },
            }));
        } catch (e) {
            message.error("Failed to fetch states");
        }
    }

    /** Fetch cities */
    async function fetchAllCities(addressType: "present" | "permanent", country: string, state: string) {
        try {
            form.setFieldsValue({
                address: {
                    ...form.getFieldValue("address"),
                    [addressType]: {...form.getFieldValue(["address", addressType]), city: ""},
                },
            });
            const cityResponse = await server.utils.countriesnow.getCities(country, state);
            const allCities = cityResponse.data.map((c: any) => c);

            setAddressConfig((prev) => ({
                ...prev,
                selectedAddress: {
                    ...prev.selectedAddress,
                    [`${addressType}Address`]: {
                        ...prev.selectedAddress[`${addressType}Address`],
                        cities: allCities,
                    },
                },
            }));
        } catch (e) {
            message.error("Failed to fetch cities");
        }
    }

    /** Handle “Same as Present” checkbox */
    const handleCheckboxChange = async (e: any) => {
        const checked = e.target.checked;
        setAddressConfig((prev) => ({...prev, sameAsPresent: checked}));

        if (checked) {
            const present = form.getFieldValue(["address", "present"]);
            form.setFieldsValue({
                address: {
                    ...form.getFieldValue("address"),
                    permanent: {...present},
                },
            });
        }
    };

    /** Reset both addresses */
    const handleReset = () => {
        form.resetFields(["address"]);
        setAddressConfig({
            sameAsPresent: false,
            selectedAddress: initialSelectedAddress,
        });
        fetchAllCountries();
    };

    return (
        <Row gutter={14} className={'address'}>
            <Col span={10} className={'present-address'}>
                <div className={'permanent-address__header'}>
                    <h2 className={"pn__employee-create__form-title"}>Present Address</h2>
                </div>
                <div className={'form-items'}>
                    <Form.Item
                        name={["address", "present", "line1"]}
                        label="Address Line 1"
                        rules={[{required: true, message: "Please enter Address"}]}
                        className={"pn__employee-create__form-item"}
                    >
                        <Input placeholder="Enter address line 1"/>
                    </Form.Item>
                    <Form.Item
                        name={["address", "present", "line2"]}
                        label="Address Line 2"
                        className={"pn__employee-create__form-item"}
                    >
                        <Input placeholder="Enter address line 2"/>
                    </Form.Item>

                    <Form.Item
                        name={["address", "present", "country"]}
                        label="Country"
                        rules={[{required: true, message: "Please select Country"}]}
                        className={"pn__employee-create__form-item"}
                    >
                        <Select
                            placeholder="Select country"
                            showSearch
                            onChange={(country) => fetchAllStates("present", country)}
                            // options={addressConfig.selectedAddress.presentAddress.countries}
                        >
                            {addressConfig.selectedAddress.presentAddress.countries.map((country: string) => (
                                <Select.Option key={country} value={country}>
                                    {country}
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item
                        name={["address", "present", "state"]}
                        label="State"
                        rules={[{required: true, message: "Please select State"}]}
                        className={"pn__employee-create__form-item"}
                    >
                        <Select
                            placeholder="Select state"
                            showSearch
                            disabled={addressConfig.selectedAddress.presentAddress.states.length === 0}
                            onChange={(state) => {
                                const country = form.getFieldValue(["address", "present", "country"]);
                                fetchAllCities("present", country, state);
                            }}
                        >
                            {addressConfig.selectedAddress.presentAddress.states.map((state: string) => (
                                <Select.Option key={state} value={state}>
                                    {state}
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item
                        name={["address", "present", "city"]}
                        label="City"
                        rules={[{required: true, message: "Please select City"}]}
                        className={"pn__employee-create__form-item"}
                    >
                        <Select
                            placeholder="Select city"
                            showSearch
                            disabled={addressConfig.selectedAddress.presentAddress.cities.length === 0}
                        >
                            {addressConfig.selectedAddress.presentAddress.cities.map((city: string) => (
                                <Select.Option key={city} value={city}>
                                    {city}
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item
                        name={["address", "present", "zipCode"]}
                        label="Zip Code"
                        className={"pn__employee-create__form-item"}
                        rules={[
                            {required: true, message: "Please enter postal code"},
                            {
                                validator: (_, value) => {
                                    if (!value) return Promise.resolve();
                                    if (isNaN(value)) return Promise.reject("Zip code must be numeric");
                                    if (value.length !== 6)
                                        return Promise.reject("Zip code must be 6 digits");
                                    // Zip code must be exactly 6 digits
                                    return Promise.resolve();
                                },
                            },
                        ]}
                    >
                        <Input placeholder="Enter 6-digit postal code"/>
                    </Form.Item>
                </div>
            </Col>

            <Col span={10} className={'permanent-address'}>
                <div className={'permanent-address__header'}>
                    <h2 className={"pn__employee-create__form-title"}>Permanent Address</h2>
                    <Checkbox
                        checked={addressConfig.sameAsPresent}
                        onChange={handleCheckboxChange}
                        className={"pn__employee-create__form-checkbox"}
                        style={{width: 'auto'}}
                    >
                        Same as Present
                    </Checkbox>
                </div>

                <div className={'form-items'}>
                    <Form.Item
                        label="Address Line 1"
                        name={["address", "permanent", "line1"]}
                        className={"pn__employee-create__form-item"}
                        rules={[{required: true, message: "Please enter Address"}]}
                    >
                        <Input placeholder="Enter address line 1"/>
                    </Form.Item>

                    <Form.Item
                        label="Address Line 2"
                        name={["address", "permanent", "line2"]}
                        className={"pn__employee-create__form-item"}
                    >
                        <Input placeholder="Enter address line 2"/>
                    </Form.Item>

                    <Form.Item
                        label="Country"
                        name={["address", "permanent", "country"]}
                        className={"pn__employee-create__form-item"}
                        rules={[{required: true, message: "Please select Country"}]}
                    >
                        <Select
                            placeholder="Select Country"
                            showSearch
                            onChange={(country) => fetchAllStates("permanent", country)}
                        >
                            {addressConfig.selectedAddress.permanentAddress.countries.map((country: string) => (
                                <Select.Option key={country} value={country}>
                                    {country}
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item
                        label="State"
                        name={["address", "permanent", "state"]}
                        className={"pn__employee-create__form-item"}
                        rules={[{required: true, message: "Please select State"}]}
                    >
                        <Select
                            placeholder="Select State"
                            showSearch
                            disabled={addressConfig.selectedAddress.permanentAddress.states.length === 0}
                            onChange={(state) => {
                                const country = form.getFieldValue(["address", "permanent", "country"]);
                                fetchAllCities("permanent", country, state);
                            }}
                        >
                            {addressConfig.selectedAddress.permanentAddress.states.map((state: string) => (
                                <Select.Option key={state} value={state}>
                                    {state}
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item
                        label="City"
                        name={["address", "permanent", "city"]}
                        className={"pn__employee-create__form-item"}
                        rules={[{required: true, message: "Please select City"}]}
                    >
                        <Select
                            placeholder="Select City"
                            showSearch
                            disabled={addressConfig.selectedAddress.permanentAddress.cities.length === 0}
                        >
                            {addressConfig.selectedAddress.permanentAddress.cities.map((city: string) => (
                                <Select.Option key={city} value={city}>
                                    {city}
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item
                        label="Postal Code"
                        name={["address", "permanent", "zipCode"]}
                        className={"pn__employee-create__form-item"}
                        rules={[
                            {required: true, message: "Please enter postal code"},
                            {
                                validator: (_, value) => {
                                    if (!value) return Promise.resolve();
                                    if (isNaN(value)) return Promise.reject("Postal code must be numeric");
                                    if (value.length !== 6)
                                        return Promise.reject("Postal code must be 6 digits");
                                    return Promise.resolve();
                                },
                            },
                        ]}
                    >
                        <Input placeholder="Enter 6-digit postal code"/>
                    </Form.Item>
                </div>
            </Col>
        </Row>
    );
};

export default AddressView;
